import AddIcon from '@mui/icons-material/Add';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { MouseEvent, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDomainCreateMutation, useDomainDestroyMutation, useDomainListQuery } from '../api/domain.js';
import { useSnapshotCreateMutation } from '../api/snapshot.js';

export function DomainsView() {
  const { projectId } = useParams();
  if (!projectId) throw new Error('Missing parameter');

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const snapshotCreate = useSnapshotCreateMutation('dns');
  const domainDestroy = useDomainDestroyMutation(projectId);
  const domainCreate = useDomainCreateMutation(projectId);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [confirmingDomainId, setConfirmingDomainId] = useState<string | null>(null);

  const handleCreateClick = () => {
    setNewDomain('');
    setNameError(undefined);
    setIsCreateOpen(true);
  };

  const handleRowClick = (domainId: string) => {
    navigate(`/projects/${projectId}/domains/${domainId}`);
  };

  const handleDeleteClick = (domainId: string) => {
    setConfirmingDomainId(domainId);
  };

  const handleDnsSnapshot = (domainId: string, event: MouseEvent) => {
    event.stopPropagation();
    snapshotCreate.mutate(
      { projectId, resourceId: domainId },
      {
        onSuccess: () => enqueueSnackbar('DNS snapshot queued', { variant: 'success' }),
        onError: (error) =>
          enqueueSnackbar(
            `Failed to queue DNS snapshot: ${error instanceof Error ? error.message : String(error)}`,
            { variant: 'error' }
          )
      }
    );
  };

  const creatingDomainId = snapshotCreate.variables?.resourceId;
  const deletingDomainId = domainDestroy.variables?.domainId;
  const creating = domainCreate.isLoading;
  const handleConfirmDelete = () => {
    if (!confirmingDomainId) return;
    domainDestroy.mutate(
      { domainId: confirmingDomainId },
      {
        onSuccess: () => enqueueSnackbar('Domain deleted', { variant: 'success' }),
        onError: (err) =>
          enqueueSnackbar(`Failed to delete domain: ${err instanceof Error ? err.message : String(err)}`, {
            variant: 'error'
          }),
        onSettled: () => setConfirmingDomainId(null)
      }
    );
  };

  const { data: domains, isLoading, isError } = useDomainListQuery(projectId);
  if (isLoading) {
    return (
      <Backdrop open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (isError || !domains) {
    return <Alert severity="error">Unable to load domains. Please try again later.</Alert>;
  }

  return (
    <Paper elevation={1} sx={{ padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Domains</Typography>
        <Button variant="contained" color="primary" onClick={handleCreateClick} startIcon={<AddIcon />}>
          {creating ? 'Creating...' : 'Create Domain'}
        </Button>
      </Box>
      <Dialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Create Domain</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Domain name"
            fullWidth
            value={newDomain}
            error={!!nameError}
            helperText={nameError}
            onChange={(e) => {
              setNewDomain(e.target.value);
              if (nameError) setNameError(undefined);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              const value = newDomain.trim();
              if (!value) {
                setNameError('Name is required');
                return;
              }
              domainCreate.mutate(value, {
                onSuccess: () => {
                  enqueueSnackbar('Domain created', { variant: 'success' });
                  setIsCreateOpen(false);
                },
                onError: (err) => setNameError(err instanceof Error ? err.message : String(err))
              });
            }}
            disabled={creating}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!confirmingDomainId} onClose={() => setConfirmingDomainId(null)}>
        <DialogTitle>Delete Domain</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete domain {confirmingDomainId}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingDomainId(null)}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete} disabled={domainDestroy.isLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {domains.map((domain) => (
              <TableRow
                key={domain.id}
                hover
                onClick={() => handleRowClick(domain.id)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{domain.id}</TableCell>
                <TableCell>{domain.name}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Snapshot" arrow>
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Snapshot"
                        disabled={snapshotCreate.isLoading && creatingDomainId === domain.id}
                        onClick={(event) => handleDnsSnapshot(domain.id, event)}
                      >
                        <CameraAltIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Delete"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(domain.id);
                        }}
                        disabled={domainDestroy.isLoading && deletingDomainId === domain.id}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
